import BaseOption from "%/dynamic-component-editor/classes/base-option";
import { inspectorTools } from "%/dynamic-component-editor/inspector/inspector-tool-map";
import { camelToTitleCase } from "@bloglovin/vue-framework";

import { useStudioStoryTypePagesStore } from "@studio/Stores/studio-story-type-pages-store";
import { useStoryTypeRenderConfigStore } from "%/dynamic-component-editor/stores/story-type-render-config-store";
import { storyTypeLabel } from "%/dynamic-components/render-engine/constants/story-type-metadata-fields";

const OPTION_KEY = "feed-v2selection";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useConfigurationStore = (...args) => require("%/dynamic-component-editor/stores/configuration-store").useConfigurationStore(...args);


// The destination whose enabled story types gate the per-type feeds. Same
// resolution FeedV2Element uses for its feed owner.
function getPublicationId() {
  return Number(useConfigurationStore().publicationId)
    || Number(window.blclient?.data?.themeDestinationId)
    || 0;
}

// Enabled story-type keys, kicking off the async load on first ask. `null` means
// the catalogue hasn't loaded — distinct from [] meaning nothing is enabled.
function readEnabledStoryTypeKeys() {
  let renderConfigStore;
  let store;
  try {
    renderConfigStore = useStoryTypeRenderConfigStore();
    store = useStudioStoryTypePagesStore();
  } catch (_error) {
    return null; // no Pinia in scope (unit-instantiated option)
  }
  if (renderConfigStore.hasRenderConfig) {
    return renderConfigStore.enabledStoryTypeKeys;
  }
  const publicationId = getPublicationId();
  if (publicationId && store.publicationId !== publicationId) {
    store.initialize(publicationId);
  }
  if (!Array.isArray(store.storyTypes) || store.storyTypes.length === 0) {
    return null;
  }
  return (store.enabledStoryTypes ?? []).map(storyType => storyType.key);
}

// Selectable publication feeds. A per-type story feed shows only while its type is
// ENABLED, except: the current value always stays, and an unloaded catalogue offers all.
function getPublicationFeedFields(selectedFieldName) {
  const fields = window.blclient?.data?.legacyEntities?.Publication?.fields ?? {};
  const enabledStoryTypeKeys = readEnabledStoryTypeKeys();
  const enabled = enabledStoryTypeKeys === null ? null : new Set(enabledStoryTypeKeys);
  return Object.entries(fields)
      .filter(([, fieldDef]) => fieldDef?.type === "manyEntities")
      .filter(([fieldName, fieldDef]) => !fieldDef.storyType
        || enabled === null
        || fieldName === selectedFieldName
        || enabled.has(fieldDef.storyType))
      .map(([fieldName, fieldDef]) => [fieldName, fieldDef.storyType ?? ""]);
}

function feedFieldName(fieldName, storyType) {
  if (!storyType) {
    return camelToTitleCase(fieldName, true);
  }
  return `${storyTypeLabel(storyType)} Stories`;
}

export default class FeedV2Selection extends BaseOption {
  static userInterfaceComponent = optionValues => ({
    label: "Feed",
    // Rendered as the "Source" row inside GlobalFeedToolSet (one "Feed" group), not standalone.
    component: inspectorTools.NONE,
    props: {
      // Matches the class-derived optionKey (SharedBaseOption kebabs the class
      // name: FeedV2Selection → "feed-v2selection"; the digit boundary in "V2"
      // takes no extra hyphen). Keep in sync with the persisted option key.
      "data-test": OPTION_KEY,
      options: getPublicationFeedFields(optionValues?.[`default.${OPTION_KEY}`])
          .map(([fieldName, storyType]) => ({
            id: fieldName,
            name: feedFieldName(fieldName, storyType),
          })),
    },
  });

  static defaultValue = "";
}
